import type { FC, SVGProps, PropsWithChildren } from "react"

import { useLongPress } from "react-use"
import type { RecoilState, SetterOrUpdater } from "recoil"
import { useRecoilState } from "recoil"

import type { RecoilStateFamily } from "../../utils"
import { Wrapper } from "../../utils/Wrapper"
import type { RadioProps } from "../Radio"

export type OptionProps<T extends number | string> = PropsWithChildren<{
  value: T
  chooseMe: () => void
  isDisabled: boolean
  isChecked: boolean
  getName: (option: T) => string
  getSummary?: (option: T) => string
  icon: RadioProps<T>[`icon`]
  closeMe?: () => void
}>

export const Option = <T extends number | string>({
  value,
  chooseMe,
  isDisabled,
  isChecked,
  getSummary,
  getName,
  icon,
  closeMe,
  children,
}: OptionProps<T>): ReturnType<FC<OptionProps<T>>> => {
  return (
    <label key={value} data-role="option">
      <input
        type="radio"
        value={value ?? ``}
        checked={isChecked}
        onChange={(e) => (chooseMe(), e.target.blur())}
        disabled={isDisabled}
        data-testid={`nested-radio-option-${value}`}
      />
      <div data-role="panel">
        {(() => {
          if (icon) {
            const Icon = icon.get(value)
            const propsObject =
              typeof icon.props === `function` ? icon.props(value) : icon.props
            const props = isChecked
              ? propsObject?.active
              : isDisabled
                ? propsObject?.disabled
                : propsObject?.inactive

            return <Icon {...props} />
          }
        })()}
      </div>
      {!isDisabled && getSummary && (
        <div data-role="tooltip">
          <div>
            <h3>{getName(value)}</h3>
            <p>{getSummary(value)}</p>
          </div>
        </div>
      )}
      <span data-role="option-name">{getName(value)}</span>
      {children}
    </label>
  )
}

export interface OptionWithTrayProps<T extends number | string>
  extends Omit<RadioProps<T>, `state`> {
  lastUsedIndexState: RecoilState<number>
  isOpen: boolean
  openMe: () => void
  closeMe: () => void
  value: T | null
  setValue: SetterOrUpdater<T | null>
  icon: NestedRadioProps<T>[`icon`]
}

export const OptionWithTray = <T extends number | string>({
  options,
  icon,
  getName = (v) => v.toString(),
  getSummary,
  shouldDisable,
  shouldHide,
  lastUsedIndexState,
  isOpen,
  openMe,
  value,
  setValue,
  closeMe,
}: OptionWithTrayProps<T>): ReturnType<FC<OptionWithTrayProps<T>>> => {
  const { TrayIndicator } = icon
  const [lastUsedIndex, setLastUsedIndex] = useRecoilState(lastUsedIndexState)
  const lastUsedValue = options[lastUsedIndex]
  const isDisabled = shouldDisable?.(lastUsedValue) ?? false
  const isHidden = shouldHide?.(lastUsedValue) ?? false
  const { onMouseDown, onMouseUp, onMouseLeave } = useLongPress(openMe)

  return (
    <div
      data-role="option-with-tray-wrapper"
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
    >
      <Option
        value={lastUsedValue}
        chooseMe={() => {
          setValue(lastUsedValue)
          closeMe()
        }}
        isDisabled={isDisabled}
        isChecked={value === lastUsedValue}
        getName={getName}
        getSummary={getSummary}
        icon={icon}
        closeMe={isOpen ? closeMe : undefined}
      >
        <span data-role="tray-indicator">
          <TrayIndicator />
        </span>
      </Option>
      {isOpen && (
        <div data-role="tray">
          {options.map((optionValue, idx) => (
            <Option
              key={optionValue}
              value={optionValue}
              chooseMe={() => (setValue(optionValue), setLastUsedIndex(idx))}
              isDisabled={isDisabled}
              isChecked={value === optionValue}
              getName={getName}
              icon={icon}
              closeMe={closeMe}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export interface NestedRadioProps<T extends number | string>
  extends Omit<RadioProps<T>, `options`> {
  trayState: {
    findLastUsedIndexState: RecoilStateFamily<number, T[]>
    openTrayState: RecoilState<T[] | null>
  }
  options: ReadonlyArray<T | T[]>
  icon: RadioProps<T>[`icon`] & { TrayIndicator: FC<SVGProps<SVGSVGElement>> }
}

export const NestedRadio = <T extends number | string>({
  label,
  options,
  state,
  trayState,
  getSummary,
  css,
  icon,
  getName = (v) => v.toString(),
  shouldDisable,
  shouldHide,
}: NestedRadioProps<T>): ReturnType<FC<NestedRadioProps<T>>> => {
  const [value, setValue] = useRecoilState(state as RecoilState<T | null>)
  const [openTray, setOpenTray] = useRecoilState(trayState.openTrayState)

  return (
    <Wrapper data-testid="nested-radio" css={css}>
      {label && <label data-role="main">{label}</label>}
      {options.map((v) => {
        if (Array.isArray(v)) {
          const lastUsedIndexState = trayState.findLastUsedIndexState(v)

          return (
            <OptionWithTray
              key={v.join()}
              lastUsedIndexState={lastUsedIndexState}
              isOpen={v === openTray}
              openMe={() => setOpenTray(v)}
              closeMe={() => setOpenTray(null)}
              value={value}
              setValue={(v) => {
                setValue(v)
                setOpenTray(null)
              }}
              getSummary={getSummary}
              options={v}
              icon={icon}
              getName={getName}
              shouldDisable={shouldDisable}
              shouldHide={shouldHide}
            />
          )
        }
        const isDisabled = shouldDisable?.(v) ?? false
        const isHidden = shouldHide?.(v) ?? false

        return isHidden ? null : (
          <Option
            key={v}
            value={v}
            chooseMe={() => {
              setValue(v)
              setOpenTray(null)
            }}
            isDisabled={isDisabled}
            isChecked={v === value}
            getSummary={getSummary}
            getName={getName}
            icon={icon}
            closeMe={() => setOpenTray(null)}
          />
        )
      })}
    </Wrapper>
  )
}
